import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { authorizedFetch } from '../lib/api'
import type { TalentProfilePreview } from '@/lib/talent-display'

export interface CVDocument {
  id: string
  original_filename: string
  file_size: number
  parsing_status: 'pending' | 'processing' | 'completed' | 'failed'
  parsing_error?: string | null
  parsed_profile_id?: string | null
  uploaded_at: string
  parsed_at?: string | null
  profile_preview?: TalentProfilePreview | null
}

export function useUploadCV() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      if (!token) throw new Error('Not authenticated')

      const formData = new FormData()
      formData.append('file', file)

      const response = await authorizedFetch('/api/profiles/cv/upload', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to upload CV')
      }

      const data = await response.json()
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv-documents'] })
      queryClient.invalidateQueries({ queryKey: ['cv-document'] })
      queryClient.invalidateQueries({ queryKey: ['files'] })
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })

  return {
    uploadCV: mutation.mutateAsync,
    uploading: mutation.isPending,
    error: mutation.error,
  }
}

export function useUploadCVs() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (files: File[]) => {
      if (!token) throw new Error('Not authenticated')

      const formData = new FormData()
      files.forEach((file) => {
        formData.append('files', file)
      })

      const response = await authorizedFetch('/api/profiles/cv/upload-batch', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to upload CVs')
      }

      const data = await response.json()
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv-documents'] })
    },
  })

  return {
    uploadCVs: mutation.mutateAsync,
    isUploading: mutation.isPending,
    uploadError: mutation.error,
  }
}

export function useCVDocuments(options?: { enabled?: boolean }) {
  const { token } = useAuth()

  const query = useQuery({
    queryKey: ['cv-documents'],
    queryFn: async (): Promise<CVDocument[]> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch('/api/profiles/cv')

      if (!response.ok) {
        throw new Error('Failed to fetch CV documents')
      }

      return response.json()
    },
    enabled: !!token && (options?.enabled ?? true),
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: (query) => {
      // Poll every 5 seconds if there are any pending or processing CVs
      const data = query.state.data as CVDocument[] | undefined
      if (data?.some((cv) => cv.parsing_status === 'pending' || cv.parsing_status === 'processing')) {
        return 5000
      }
      return false
    },
  })

  return {
    cvDocuments: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useCVDocument(cvId: string | null) {
  const { token } = useAuth()

  const query = useQuery({
    queryKey: ['cv-document', cvId],
    queryFn: async (): Promise<CVDocument> => {
      if (!token) throw new Error('Not authenticated')
      if (!cvId) throw new Error('CV ID required')

      const response = await authorizedFetch(`/api/profiles/cv/${cvId}`)

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('CV document not found')
        }
        throw new Error('Failed to fetch CV document')
      }

      return response.json()
    },
    enabled: !!token && !!cvId,
    staleTime: 10 * 1000, // 10 seconds
    retry: (_failureCount, error) => error.message !== 'CV document not found',
    refetchInterval: (query) => {
      // Poll every 5 seconds if CV is pending or processing
      const data = query.state.data as CVDocument | undefined
      if (data?.parsing_status === 'pending' || data?.parsing_status === 'processing') {
        return 5000
      }
      return false
    },
  })

  const notFound = query.error?.message === 'CV document not found'

  return {
    cvDocument: notFound ? undefined : query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useRetryCVParse() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (cvId: string) => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/profiles/cv/${cvId}/retry`, {
        method: 'POST',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to retry CV parsing')
      }

      return response.json()
    },
    onSuccess: (_data, cvId) => {
      queryClient.invalidateQueries({ queryKey: ['cv-documents'] })
      queryClient.invalidateQueries({ queryKey: ['cv-document', cvId] })
      queryClient.invalidateQueries({ queryKey: ['files'] })
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })

  return {
    retryCVParse: mutation.mutateAsync,
    isRetrying: mutation.isPending,
    error: mutation.error,
  }
}

export function useDeleteCVDocument() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (cvId: string) => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/profiles/cv/${cvId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to delete CV document')
      }

      return cvId
    },
    onSuccess: (cvId) => {
      queryClient.removeQueries({ queryKey: ['cv-document', cvId] })
      queryClient.invalidateQueries({ queryKey: ['cv-documents'] })
    },
  })

  return {
    deleteCV: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error,
  }
}
